/**
 * one-pager.js
 * Vignovic Agency one-page site, run VA-OP1.
 * Loaded once, deferred, on every template. Every module checks for its own
 * elements and does nothing when they are absent, so this file is safe on the
 * blog and case-study templates as well as the homepage.
 *
 * Modules:
 *   1 theme toggle, 2 mobile menu, 3 announcement bar, 4 header height sync,
 *   5 count-up numbers, 6 scroll reveal, 7 sticky mobile CTA,
 *   8 attribution capture, 9 form received state, 10 click events,
 *   11 icons, 12 stage tabs, 13 scorecard rounds, 14 profile explorer,
 *   15 fit check, 16 forms (steps, validation, honeypot, prefill),
 *   17 scrollspy, 18 theme-color and toggle state, 19 image resilience,
 *   20 section and scroll-depth events, 21 keyboard escapes and focus.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Analytics: one function, one event name prefix, forwarded by the custom pixel. */
  function track(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('vignovic:' + name, data || {});
      }
    } catch (e) { /* analytics must never break the page */ }
  }
  window.vaTrack = track;

  /* 1. Theme toggle. Same localStorage key as the legacy header so a saved
        preference carries across templates. */
  if (localStorage.getItem('theme') === 'light') document.body.classList.add('light-mode');
  function syncThemeState() {
    var light = document.body.classList.contains('light-mode');
    document.querySelectorAll('.op .toggle').forEach(function (b) { b.setAttribute('aria-pressed', light ? 'true' : 'false'); });
    var meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', light ? '#ffffff' : '#000000');
    document.documentElement.style.colorScheme = light ? 'light' : 'dark';
  }
  document.querySelectorAll('.op .toggle').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var light = document.body.classList.toggle('light-mode');
      localStorage.setItem('theme', light ? 'light' : 'dark');
      syncThemeState();
    });
  });
  syncThemeState();

  /* 2. Mobile menu: close the details element after a link is chosen. */
  document.querySelectorAll('.op details.menu').forEach(function (menu) {
    menu.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', function () { menu.removeAttribute('open'); });
    });
    document.addEventListener('click', function (e) { if (menu.open && !menu.contains(e.target)) menu.removeAttribute('open'); });
    menu.addEventListener('keydown', function (e) { if (e.key === 'Escape') { menu.removeAttribute('open'); menu.querySelector('summary').focus(); } });
  });

  /* 3. Announcement bar: rotate every 6 seconds, pause on hover, dismiss for the session. */
  var an = document.getElementById('announce');
  if (an) {
    if (sessionStorage.getItem('announce_closed')) an.hidden = true;
    var closeBtn = document.getElementById('announce-x');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      an.hidden = true;
      sessionStorage.setItem('announce_closed', '1');
      syncHeaderHeight();
    });
    var slides = an.querySelectorAll('.aslide'), dots = an.querySelectorAll('.adots i'),
        label = document.getElementById('alabel'), k = 0, timer;
    function go(n) {
      if (slides.length < 2) return;
      slides[k].classList.remove('on'); if (dots[k]) dots[k].classList.remove('on');
      k = n % slides.length;
      slides[k].classList.add('on'); if (dots[k]) dots[k].classList.add('on');
      if (label) label.textContent = slides[k].getAttribute('data-label') || '';
    }
    function start() { if (reduceMotion || slides.length < 2) return; timer = setInterval(function () { go(k + 1); }, 6000); }
    an.addEventListener('mouseenter', function () { clearInterval(timer); });
    an.addEventListener('mouseleave', start);
    start();
  }

  /* 4. Header height sync: the whole header group is sticky, so anchor targets
        need scroll-padding equal to its height. */
  var headerGroup = document.getElementById('header-group');
  function syncHeaderHeight() {
    if (!headerGroup) return;
    var h = headerGroup.getBoundingClientRect().height;
    document.documentElement.style.scrollPaddingTop = Math.round(h + 16) + 'px';
    document.documentElement.style.setProperty('--op-header-h', Math.round(h) + 'px');
  }
  if (headerGroup) {
    syncHeaderHeight();
    if ('ResizeObserver' in window) new ResizeObserver(syncHeaderHeight).observe(headerGroup);
    else window.addEventListener('resize', syncHeaderHeight);
  }

  /* 5. Count-up numbers: <b data-count="6" data-suffix="M+" data-prefix="">. */
  document.querySelectorAll('.op [data-count]').forEach(function (el) {
    var target = parseFloat(el.getAttribute('data-count')), suffix = el.getAttribute('data-suffix') || '',
        prefix = el.getAttribute('data-prefix') || '', decimals = (String(target).split('.')[1] || '').length, done = false;
    if (isNaN(target)) return;
    function run() {
      if (done) return; done = true;
      if (reduceMotion) { el.textContent = prefix + target + suffix; return; }
      var t0 = performance.now();
      (function tick(t) {
        var p = Math.min(1, (t - t0) / 1200); p = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + (target * p).toFixed(decimals) + suffix;
        if (p < 1) requestAnimationFrame(tick); else el.textContent = prefix + target + suffix;
      })(t0);
    }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { if (en[0].isIntersecting) run(); }, { threshold: 0.5 }).observe(el);
    } else run();
  });

  /* 6. Scroll reveal: desktop pointer devices only, never with reduced motion. */
  if ('IntersectionObserver' in window && !reduceMotion && window.matchMedia('(hover:hover) and (min-width:700px)').matches
      && document.querySelector('.op section > .wrap')) {
    document.documentElement.classList.add('rv');
    var io = new IntersectionObserver(function (en) {
      en.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.08 });
    document.querySelectorAll('.op section > .wrap, .op .marquee').forEach(function (el) { io.observe(el); });
  }

  /* 7. Sticky mobile CTA: hide while the audit section is on screen. */
  var sticky = document.getElementById('sticky'), auditSection = document.getElementById('audit');
  if (sticky && auditSection && 'IntersectionObserver' in window) {
    new IntersectionObserver(function (en) { sticky.classList.toggle('off', en[0].isIntersecting); }, { threshold: 0.15 }).observe(auditSection);
  }

  /* 8. Attribution capture: UTM parameters, referrer, and landing path are kept
        for the session and copied into any input marked data-attr="key". */
  var q = new URLSearchParams(location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'].forEach(function (key) {
    if (q.get(key)) sessionStorage.setItem('va_' + key, q.get(key));
  });
  if (document.referrer && !sessionStorage.getItem('va_referrer')) sessionStorage.setItem('va_referrer', document.referrer);
  if (!sessionStorage.getItem('va_landing_path')) sessionStorage.setItem('va_landing_path', location.pathname + location.search);
  document.querySelectorAll('.op input[data-attr]').forEach(function (input) {
    var key = input.getAttribute('data-attr');
    input.value = sessionStorage.getItem('va_' + key) || '';
  });

  /* 9. Form received state. Forms post to the form service and redirect back to
        /?<form>=received#<anchor>. Show the confirmation, publish the event once,
        then clean the query string so a refresh does not double count. */
  var received = null;
    ['audit', 'scorecard', 'digest'].forEach(function (name) { if (q.get(name) === 'received') received = name; });
    /* Shopify contact form path: the page reloads with contact_posted=true and every
       contact form on it reports success, so the form that actually posted is the one
       remembered at submit time. The other form's success block is removed. */
    if (q.get('contact_posted') === 'true') {
      var last = null; try { last = sessionStorage.getItem('va_last_form'); } catch (x) {}
      document.querySelectorAll('.op .ok[data-posted]').forEach(function (box) {
        var name = box.getAttribute('data-posted');
        if (last && name !== last) { box.remove(); return; }
        received = name;
        var frm = box.closest('form'); if (frm) Array.prototype.slice.call(frm.children).forEach(function (child) { if (child !== box) child.hidden = true; });
      });
      try { sessionStorage.removeItem('va_last_form'); } catch (x) {}
      q.delete('contact_posted');
    }

  if (received) {
    var okBox = document.querySelector('.op .ok[data-form="' + received + '"]');
          var wrap = document.querySelector('.op [data-form-wrap="' + received + '"]');
      if (wrap && !document.querySelector('.op .ok[data-posted="' + received + '"]')) wrap.hidden = true;
    if (okBox) { okBox.classList.add('on'); okBox.scrollIntoView({ block: 'center' }); }
    track('lead_submitted', { form: received, landing_path: sessionStorage.getItem('va_landing_path') || '', utm_source: sessionStorage.getItem('va_utm_source') || '' });
    if (window.history && history.replaceState) {
      q.delete(received);
      var clean = location.pathname + (q.toString() ? '?' + q.toString() : '') + location.hash;
      history.replaceState(null, '', clean);
    }
  }

  /* 10. Event hooks on CTAs: any element with data-track="name" publishes a click event. */
  document.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('.op [data-track]') : null;
    if (el) track('cta_click', { name: el.getAttribute('data-track'), href: el.getAttribute('href') || '' });
  });

  /* 11. Decorative icons for profile tiles and quiz options. */
  var ICON = {
    apparel: '<path d="M4 7l4-3 2 2h4l2-2 4 3-2 3-2-1v11H8V9l-2 1z"/>',
    nutrition: '<path d="M7 17a4 4 0 0 1 0-6l4-4a4 4 0 1 1 6 6l-4 4a4 4 0 0 1-6 0z"/><path d="M9 9l6 6"/>',
    coffee: '<path d="M5 8h11v6a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5z"/><path d="M16 10h2a2 2 0 0 1 0 4h-2"/><path d="M8 3v2M12 3v2"/>',
    beauty: '<path d="M12 3s6 6 6 11a6 6 0 0 1-12 0c0-5 6-11 6-11z"/><path d="M9 14a3 3 0 0 0 3 3"/>',
    launch: '<path d="M12 3c3 2 5 6 5 10l-2 2H9l-2-2c0-4 2-8 5-10z"/><circle cx="12" cy="9" r="1.5"/><path d="M9 15l-3 3M15 15l3 3M12 15v5"/>',
    industrial: '<circle cx="12" cy="12" r="3.5"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.2 2.2M16.9 16.9l2.2 2.2M4.9 19.1l2.2-2.2M16.9 7.1l2.2-2.2"/>',
    saas: '<path d="M8 9l-4 3 4 3M16 9l4 3-4 3M14 5l-4 14"/>',
    services: '<path d="M3 8h18v11H3z"/><path d="M9 8V5h6v3M3 13h18"/>',
    home: '<path d="M12 4L2 20h20L12 4z"/><path d="M12 4v16"/>',
    wholesale: '<path d="M3 12h8v8H3zM13 12h8v8h-8zM8 4h8v8H8z"/>',
    hospitality: '<path d="M5 3v7a2 2 0 0 0 4 0V3M7 10v11M17 3c-2 0-3 3-3 6v3h3v9"/>',
    franchise: '<path d="M4 21V8l8-5 8 5v13"/><path d="M9 21v-6h6v6M8 12h2M14 12h2"/>',
    political: '<path d="M3 10v3a2 2 0 0 0 2 2h1l3 5h2v-5l8 2V4l-8 2H5a2 2 0 0 0-2 2z"/>',
    athlete: '<path d="M8 4h8v5a4 4 0 0 1-8 0V4z"/><path d="M8 6H5a3 3 0 0 0 3 3M16 6h3a3 3 0 0 1-3 3M12 13v4M9 20h6"/>',
    executive: '<path d="M12 3a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6"/>',
    other: '<path d="M12 5v14M5 12h14"/>'
  };
  document.querySelectorAll('.op .ico-slot[data-icon]').forEach(function (slot) {
    var key = slot.getAttribute('data-icon');
    slot.innerHTML = '<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICON[key] || ICON.other) + '</svg>';
  });

  /* 12. Stage tabs: panels are rendered by Liquid; this only switches them. */
  document.querySelectorAll('.op [data-stages]').forEach(function (box) {
    var tabs = box.querySelectorAll('[role="tab"]'), panels = box.querySelectorAll('[role="tabpanel"]');
    function activate(i) {
      tabs.forEach(function (t, k) { t.classList.toggle('on', k === i); t.setAttribute('aria-selected', k === i ? 'true' : 'false'); t.tabIndex = k === i ? 0 : -1; });
      panels.forEach(function (p, k) { p.hidden = k !== i; });
      track('stage_viewed', { stage: tabs[i].getAttribute('data-stage') || String(i + 1) });
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(i); });
      t.addEventListener('keydown', function (e) {
        var n = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
        if (n === null) return; e.preventDefault(); n = (n + tabs.length) % tabs.length; activate(n); tabs[n].focus();
      });
    });
    tabs.forEach(function (t, k) { t.tabIndex = k === 0 ? 0 : -1; });
  });

  /* 13. Scorecard rounds. */
  document.querySelectorAll('.op [data-standard]').forEach(function (std) {
    var rows = std.querySelectorAll('tbody tr[data-round]'), details = std.querySelectorAll('[data-round-detail]');
    function round(i) {
      rows.forEach(function (r, k) { r.classList.toggle('on', k === i); });
      details.forEach(function (d, k) { d.hidden = k !== i; });
    }
    rows.forEach(function (r, i) { r.tabIndex = 0; r.addEventListener('click', function () { round(i); }); r.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); round(i); } }); });
    if (window.matchMedia('(hover:hover)').matches) std.addEventListener('mouseover', function (e) { var t = e.target.closest('tr[data-round]'); if (t) round(+t.getAttribute('data-round')); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { if (en[0].isIntersecting) { std.classList.add('in'); std.querySelectorAll('.sc-table td span').forEach(function (x, k) { x.style.transitionDelay = (k * 60) + 'ms'; }); } }, { threshold: 0.35 }).observe(std);
    } else std.classList.add('in');
  });

  /* 14. Profile explorer: tiles open the matching Liquid-rendered panel. */
  var tiles = document.querySelector('.op [data-profile-tiles]'), detail = document.querySelector('.op [data-profile-detail]');
  if (tiles && detail) {
    var tileBtns = tiles.querySelectorAll('[data-profile]'), panels = detail.querySelectorAll('[data-profile-panel]');
    function select(key) {
      tileBtns.forEach(function (t) { t.classList.toggle('on', t.getAttribute('data-profile') === key); });
      panels.forEach(function (p) { p.hidden = p.getAttribute('data-profile-panel') !== key; });
      detail.hidden = false;
      detail.focus({ preventScroll: true });
      var r = detail.getBoundingClientRect();
      if (r.top < 90 || r.bottom > window.innerHeight) detail.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      track('profile_viewed', { profile: key });
    }
    tileBtns.forEach(function (t) { t.addEventListener('click', function () { select(t.getAttribute('data-profile')); }); });
    function closeDetail() { detail.hidden = true; var on = tiles.querySelector('.tile.on'); tileBtns.forEach(function (t) { t.classList.remove('on'); }); if (on) on.focus(); }
    detail.addEventListener('click', function (e) { if (e.target.closest('.dclose')) closeDetail(); });
    detail.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDetail(); });
    detail.tabIndex = -1;
  }

  /* 15. Fit check. Questions, options, scores, thresholds, and copy all come
         from the section; this is only the state machine. */
  var quiz = document.querySelector('.op [data-quiz]');
  if (quiz) {
    var copy = {}, profiles = [];
    try { copy = JSON.parse(quiz.querySelector('[data-quiz-copy]').textContent); } catch (e) {}
    try { profiles = JSON.parse(quiz.querySelector('[data-profiles]').textContent); } catch (e) {}
    var qs = Array.prototype.slice.call(quiz.querySelectorAll('.fq')), rail = quiz.querySelectorAll('.rail li'),
        stepEl = quiz.querySelector('[data-step]'), backBtn = quiz.querySelector('[data-back]'), i = 0, ans = {}, sc = {}, started = false;
    var strong = +quiz.getAttribute('data-strong') || 70, possible = +quiz.getAttribute('data-possible') || 45,
        hardKey = quiz.getAttribute('data-hard-key') || 'revenue', hardValue = quiz.getAttribute('data-hard-value') || '',
        hardCap = +quiz.getAttribute('data-hard-cap') || 40, auditAnchor = quiz.getAttribute('data-audit-anchor') || 'audit',
        scorecardAnchor = quiz.getAttribute('data-scorecard-anchor') || 'scorecard';
    function show() {
      qs.forEach(function (el, k) { el.classList.toggle('on', k === i); });
      rail.forEach(function (b, k) { b.classList.toggle('on', k === i); b.classList.toggle('done', k < i); });
      if (stepEl) stepEl.textContent = (i + 1) + ' of ' + qs.length;
      if (backBtn) backBtn.hidden = i === 0;
    }
    function advance() { if (i < qs.length - 1) { i++; show(); } else { result(); } }
    quiz.querySelector('[data-qbody]').addEventListener('click', function (e) {
      var nx = e.target.closest('.next');
      if (nx) { var fq0 = nx.closest('.fq'), k0 = fq0.getAttribute('data-q'); if (!(ans[k0] && ans[k0].length)) return; advance(); return; }
      var b = e.target.closest('.opt'); if (!b) return;
      if (!started) { started = true; track('fit_check_started', {}); }
      var fq = b.closest('.fq'), key = fq.getAttribute('data-q');
      if (fq.classList.contains('multi')) {
        var max = +fq.getAttribute('data-max') || 99, sel = fq.querySelectorAll('.opt.sel');
        if (b.hasAttribute('data-x')) { fq.querySelectorAll('.opt').forEach(function (o) { o.classList.remove('sel'); }); b.classList.add('sel'); }
        else {
          fq.querySelectorAll('.opt[data-x]').forEach(function (o) { o.classList.remove('sel'); });
          if (b.classList.contains('sel')) b.classList.remove('sel'); else if (sel.length < max) b.classList.add('sel');
        }
        var picked = Array.prototype.slice.call(fq.querySelectorAll('.opt.sel'));
        ans[key] = picked.map(function (o) { return o.getAttribute('data-v'); });
        ans[key + '__d'] = picked.map(function (o) { return o.getAttribute('data-d') || ''; });
        var scores = picked.map(function (o) { return +o.getAttribute('data-s') || 0; });
        if (fq.hasAttribute('data-sum')) sc[key] = Math.min(+fq.getAttribute('data-sum') || 99, scores.reduce(function (a, x) { return a + x; }, 0));
        else sc[key] = Math.max.apply(null, scores.concat([0]));
        return;
      }
      ans[key] = b.getAttribute('data-v'); ans[key + '__label'] = b.getAttribute('data-label') || b.textContent.trim(); sc[key] = +b.getAttribute('data-s') || 0;
      fq.querySelectorAll('.opt').forEach(function (o) { o.classList.remove('sel'); }); b.classList.add('sel'); advance();
    });
    if (backBtn) backBtn.addEventListener('click', function () { if (i > 0) { i--; show(); } });
    var redo = quiz.querySelector('[data-redo]');
    if (redo) redo.addEventListener('click', function () { i = 0; ans = {}; sc = {}; quiz.classList.remove('done'); quiz.querySelectorAll('.opt.sel').forEach(function (o) { o.classList.remove('sel'); }); show(); quiz.scrollIntoView({ block: 'start' }); });
    function divisionFor() {
      var c = ans.constraint || [], d = ans.constraint__d || [];
      if (!c.length || c.indexOf('unsure') > -1) return quiz.getAttribute('data-unsure-division') || '';
      var list = d.filter(function (v, k, a) { return v && a.indexOf(v) === k; });
      return list.length > 1 ? list.slice(0, -1).join(', ') + ' with ' + list.slice(-1) : (list[0] || '');
    }
    function setR(key, text) { var el = quiz.querySelector('[data-r="' + key + '"]'); if (el) el.textContent = text || ''; }
    function result() {
      var total = Object.keys(sc).reduce(function (a, k) { return a + sc[k]; }, 0);
      var p = profiles.filter(function (x) { return x.key === ans.icp; })[0] || profiles[0] || {};
      var hard = hardValue && ans[hardKey] === hardValue, grade, why, sub, cta = quiz.querySelector('[data-rcta]');
      cta.innerHTML = '';
      if (hard) { total = Math.min(total, hardCap); grade = copy.hard.grade; why = copy.hard.why; sub = copy.hard.sub; }
      else if (total >= strong) { grade = copy.strong.grade; why = copy.strong.why; sub = copy.strong.sub; }
      else { grade = copy.possible.grade; why = p.soft ? copy.possible.whySoft : copy.possible.why; sub = copy.possible.sub; }
      var gradeEl = quiz.querySelector('[data-rgrade]'); gradeEl.textContent = grade || ''; var small = document.createElement('small'); small.textContent = sub || ''; gradeEl.appendChild(small);
      quiz.querySelector('[data-rwhy]').textContent = why || '';
      setR('icp', p.soft ? copy.outside : (p.name || '') + (p.band ? ' (' + p.band + ')' : ''));
      setR('division', divisionFor() || p.division);
      setR('first', (ans.constraint || []).indexOf('unsure') > -1 ? quiz.getAttribute('data-unsure-text') : p.first);
      setR('model', hard ? quiz.getAttribute('data-hard-model') : p.model);
      setR('channels', (ans.channels || []).filter(function (x) { return x !== 'none'; }).join(', ') || copy.noneYet);
      setR('measurement', ans.measurement__label || ans.measurement || '');
      var a = document.createElement('a'); a.className = 'btn btn-p';
      if (hard) { a.href = '#' + scorecardAnchor; a.textContent = copy.ctaScorecard || 'Get the Scorecard'; a.setAttribute('data-track', 'fit_to_scorecard'); }
      else { a.href = '#' + auditAnchor; a.textContent = copy.ctaAudit || 'Request an audit'; a.setAttribute('data-track', 'fit_to_audit'); a.addEventListener('click', function () { prefill(p, total); }); }
      cta.appendChild(a);
      quiz.classList.add('done'); quiz.scrollIntoView({ block: 'start' });
      var resBox = quiz.querySelector('[data-result]'); if (resBox) { resBox.tabIndex = -1; resBox.focus({ preventScroll: true }); }
      animate(total);
      track('fit_check_completed', { score: total, grade: grade, icp: p.key || '', revenue: ans.revenue || '' });
    }
    function animate(n) {
      var fg = quiz.querySelector('[data-ring]'), num = quiz.querySelector('[data-rnum]'), C = 326.7;
      fg.style.strokeDashoffset = C; num.textContent = '0';
      requestAnimationFrame(function () { requestAnimationFrame(function () { fg.style.strokeDashoffset = C * (1 - n / 100); }); });
      if (reduceMotion) { num.textContent = n; return; }
      var t0 = performance.now();
      (function tick(t) { var k = Math.min(1, (t - t0) / 1100); k = 1 - Math.pow(1 - k, 3); num.textContent = Math.round(n * k); if (k < 1) requestAnimationFrame(tick); })(t0);
    }
    function prefill(p, total) {
      var f = document.querySelector('.op form.lead'); if (!f) return;
      function set(key, val) { var el = f.querySelector('[data-key="' + key + '"]'); if (el && val !== undefined) el.value = val; }
      set('revenue', ans.revenue); set('invest', ans.invest); set('timeline', ans.timeline); set('stack', ans.stack); set('team', ans.team);
      (ans.constraint || []).forEach(function (v) { var c = f.querySelector('[data-key="constraint"][value="' + v + '"]'); if (c) c.checked = true; });
      set('icp', p.name || ''); set('fit_score', String(total)); set('ready', ans.ready || ''); set('channels', (ans.channels || []).join('|')); set('measurement', ans.measurement || '');
      var pref = f.querySelector('[data-pref]'); if (pref) pref.classList.add('on');
    }
    show();
  }

  /* 16. Forms: two-step audit form, honeypot, disabled state, validation. */
  document.querySelectorAll('.op form.lead, .op form.sc-form').forEach(function (f) {
      f.setAttribute('novalidate', '');
      var steps = f.querySelectorAll('[data-step]');
    function stepOk(step) {
      var bad = null;
      step.querySelectorAll('input[required], select[required], textarea[required]').forEach(function (x) { if (!bad && !x.checkValidity()) bad = x; });
      if (bad) { bad.reportValidity(); return false; }
      var chips = step.querySelector('[data-constraints]');
      if (chips && !chips.querySelector('input:checked')) { chips.scrollIntoView({ block: 'center' }); var first = chips.querySelector('input'); if (first) { first.setCustomValidity('Pick at least one.'); first.reportValidity(); first.setCustomValidity(''); } return false; }
      return true;
    }
    f.querySelectorAll('[data-to-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-to-step'), current = btn.closest('[data-step]');
        if (target > current.getAttribute('data-step') && !stepOk(current)) return;
        steps.forEach(function (s) { s.hidden = s.getAttribute('data-step') !== target; });
        var shown = f.querySelector('[data-step="' + target + '"]');
        if (shown) { shown.scrollIntoView({ block: 'start' }); var first = shown.querySelector('input:not([type="hidden"]), select, textarea'); if (first) first.focus({ preventScroll: true }); }
        if (target === '2') track('audit_step2', {});
      });
    });
    f.addEventListener('submit', function (e) {
      var hp = f.querySelector('[data-honeypot-field]');
      if (f.getAttribute('data-disabled') === 'true' || (hp && hp.value)) { e.preventDefault(); return; }
      var ok = true;
      steps.forEach(function (s) { if (ok && !stepOk(s)) ok = false; });
      if (!steps.length && !f.checkValidity()) { f.reportValidity(); ok = false; }
      if (!ok) { e.preventDefault(); return; }
        var joined = f.querySelector('[data-key="constraint_list"]');
        if (joined) joined.value = Array.prototype.slice.call(f.querySelectorAll('[data-key="constraint"]:checked')).map(function (c) { return c.value; }).join(', ');
        try { sessionStorage.setItem('va_last_form', f.id.replace('-form', '')); } catch (x) {}
        track('form_submit', { form: f.id || '' });

    });
  });

  /* 17. Scrollspy: the header menu marks the section on screen. Only for
         links of the form /#id or #id that point at a section on this page. */
  var navLinks = Array.prototype.slice.call(document.querySelectorAll('.op .nav nav a[href*="#"]'));
  var spyMap = [];
  navLinks.forEach(function (a) {
    var id = a.getAttribute('href').split('#')[1]; var el = id && document.getElementById(id);
    if (el) spyMap.push({ a: a, el: el });
  });
  if (spyMap.length && 'IntersectionObserver' in window) {
    var current = null;
    var spy = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        spyMap.forEach(function (m) {
          var on = m.el === en.target;
          if (on) { m.a.setAttribute('aria-current', 'true'); current = m.el; } else m.a.removeAttribute('aria-current');
        });
      });
    }, { rootMargin: '-40% 0px -50% 0px', threshold: 0 });
    spyMap.forEach(function (m) { spy.observe(m.el); });
  }

  /* 18. (merged into module 1: aria-pressed and meta theme-color) */

  /* 19. Image resilience: a failed image shows the frame's background
         instead of a broken icon; a loaded image fades in. */
  document.querySelectorAll('.op .frame > img').forEach(function (img) {
    if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) img.closest('.frame').classList.add('frame-broken');
    img.addEventListener('error', function () { img.closest('.frame').classList.add('frame-broken'); });
  });

  /* 20. Section and scroll-depth events for the Signal Dashboard. */
  if ('IntersectionObserver' in window) {
    var seen = {};
    var secObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var id = en.target.id;
        if (en.isIntersecting && !seen[id]) { seen[id] = true; track('section_viewed', { section: id }); secObs.unobserve(en.target); }
      });
    }, { threshold: 0.3 });
    document.querySelectorAll('.op section[id]').forEach(function (sec) { secObs.observe(sec); });
  }
  var marks = [25, 50, 75, 100], fired = {};
  function depth() {
    var h = document.documentElement, max = h.scrollHeight - window.innerHeight; if (max <= 0) return;
    var pct = Math.round((window.scrollY / max) * 100);
    marks.forEach(function (m) { if (pct >= m && !fired[m]) { fired[m] = true; track('scroll_depth', { percent: m }); } });
  }
  window.addEventListener('scroll', depth, { passive: true }); depth();

  /* 21. Announcement dismiss with the keyboard is native (button). Nothing else needed. */
})();
